<form [formGroup]="discussionForm" class="discussion-create--form">
  <!-- Choose Category -->
  <div class="form-group">
    <label class="form-label">
      Choose Category <span class="required-asterisk">*</span>
    </label>
    <p class="form-hint" *ngIf="isEditMode">
      Category cannot be changed when editing a discussion.
    </p>
    <div class="checkbox-group" [class.disabled]="isEditMode">
      <label
        *ngFor="let option of categoryOptions"
        class="checkbox-option"
        [class.selected]="discussionForm.get('category')?.value === option.value"
        [class.disabled]="isEditMode"
        (click)="!isEditMode && onCategoryChange(option.value); $event.preventDefault()"
      >
        <div class="checkbox-custom" [class.checked]="discussionForm.get('category')?.value === option.value"></div>
        <span class="checkbox-label">{{ option.label }}</span>
      </label>
    </div>
    <div
      *ngIf="discussionForm.get('category')?.invalid && discussionForm.get('category')?.touched"
      class="error-message"
    >
      Category is required
    </div>
  </div>

  <!-- Discussion Title (col-6) -->
  <div class="form-row">
    <div class="form-group form-group-col-6">
      <label for="title" class="form-label">
        Discussion Title <span class="required-asterisk">*</span>
      </label>
      <div class="title-input-wrapper">
        <input
          type="text"
          id="title"
          formControlName="title"
          class="form-input title-input-with-count"
          placeholder="Enter discussion title"
          maxlength="200"
        />
        <span
          class="title-char-count"
          [class.title-char-count--below-min]="titleLength < TITLE_MIN_LENGTH && titleLength > 0"
          [class.title-char-count--over-max]="titleLength > TITLE_MAX_LENGTH"
          [class.title-char-count--valid]="titleLength >= TITLE_MIN_LENGTH && titleLength <= TITLE_MAX_LENGTH"
          title="Characters: current (min–max)"
        >
          {{ titleLength }} ({{ TITLE_MIN_LENGTH }}-{{ TITLE_MAX_LENGTH }})
        </span>
      </div>
      <div
        *ngIf="discussionForm.get('title')?.invalid && discussionForm.get('title')?.touched"
        class="error-message"
      >
        <span *ngIf="discussionForm.get('title')?.errors?.['required']">Title is required</span>
        <span *ngIf="discussionForm.get('title')?.errors?.['minlength']">Title must be at least 30 characters</span>
        <span *ngIf="discussionForm.get('title')?.errors?.['maxlength']">Title cannot exceed 200 characters</span>
      </div>
    </div>
  </div>

  <!-- Choose Asset and Choose Datasets in one row (both col-6) -->
  <div class="asset-row-group">
    <!-- Choose Asset (col-6) - dropdown with checkboxes -->
    <div class="form-group asset-form-group form-group-col-6 asset-dropdown-wrapper">
      <label class="form-label">
        Choose Asset <span class="required-asterisk">*</span>
      </label>
      <button
        type="button"
        class="asset-dropdown-trigger"
        [class.open]="assetCategoryDropdownOpen"
        (click)="toggleAssetCategoryDropdown()"
      >
        <span
          class="asset-dropdown-value"
          [class.asset-dropdown-value--placeholder]="!discussionForm.get('assetCategory')?.value"
        >{{ getSelectedAssetCategoryLabel() }}</span>
        <svg
          class="asset-dropdown-icon"
          [class.open]="assetCategoryDropdownOpen"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      <div class="asset-dropdown-panel" *ngIf="assetCategoryDropdownOpen">
        <div
          *ngFor="let option of assetCategoryOptions"
          class="asset-dropdown-option"
          [class.selected]="discussionForm.get('assetCategory')?.value === option.value"
          (click)="selectAssetCategory(option.value)"
        >
          <div class="checkbox-custom" [class.checked]="discussionForm.get('assetCategory')?.value === option.value"></div>
          <span>{{ option.label }}</span>
        </div>
      </div>
    </div>

    <!-- Choose Datasets/Models/Usecases (Autocomplete) - shown when asset category is selected and not "Others" (col-6) -->
    <div
      class="form-group asset-form-group form-group-col-6"
      *ngIf="discussionForm.get('assetCategory')?.value && discussionForm.get('assetCategory')?.value !== 'Others'"
    >
      <label for="assetSearch" class="form-label">
        Choose {{ getAssetCategoryLabel(discussionForm.get('assetCategory')?.value) }} <span class="required-asterisk">*</span>
      </label>
      <div class="asset-search-wrapper">
        <input
          type="text"
          id="assetSearch"
          class="form-input asset-search-input"
          placeholder="Search {{ getAssetCategoryLabel(discussionForm.get('assetCategory')?.value) }}..."
          autocomplete="off"
          [(ngModel)]="assetSearchQuery"
          [ngModelOptions]="{standalone: true}"
          (input)="onAssetSearchInput($event)"
          (focus)="onAssetSearchFocus()"
          (blur)="onAssetSearchBlur()"
        />
        <button
          *ngIf="selectedAsset"
          type="button"
          class="asset-clear-btn"
          (click)="clearAsset()"
          title="Clear selection"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <!-- Loading Indicator -->
        <div class="asset-search-results asset-search-results--loading" *ngIf="isLoadingAssets">
          <div class="asset-search-results--loading-text">Searching...</div>
        </div>
        
        <!-- Search Results Dropdown -->
        <div class="asset-search-results" *ngIf="!isLoadingAssets && showAssetSearchResults && assetSearchResults.length > 0">
          <div
            *ngFor="let result of assetSearchResults"
            class="asset-search-result-item"
            (mousedown)="selectAsset(result); $event.preventDefault()"
          >
            <div class="asset-search-result-content">
              <div class="asset-search-result-name">{{ result.name || result.label }}</div>
              <div class="asset-search-result-organization" *ngIf="result.organization">
                {{ result.organization }}
              </div>
            </div>
            <span class="asset-search-result-type">{{ result.type }}</span>
          </div>
        </div>
        
        <!-- No Results Message -->
        <div
          class="asset-search-results asset-search-results--empty"
          *ngIf="!isLoadingAssets && assetSearchQuery.trim().length >= 2 && showAssetSearchResults && assetSearchResults.length === 0"
        >
          <div class="asset-search-results--empty-text">{{ getAssetSearchEmptyMessage() }}</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Asset Name Input (shown when asset category is "Others") -->
  <div
    class="form-group"
    *ngIf="discussionForm.get('assetCategory')?.value === 'Others'"
  >
    <label for="assetName" class="form-label">
      Asset Name
    </label>
    <input
      type="text"
      id="assetName"
      formControlName="assetName"
      class="form-input"
      placeholder="Enter asset name"
    />
  </div>

  <!-- Content Editor (col-12) -->
  <div class="form-group form-group-col-12">
    <label for="content" class="form-label">
      Content <span class="required-asterisk">*</span>
    </label>
    <div class="content-editor-wrapper">
      <textarea
        id="content"
        formControlName="content"
        class="form-textarea content-textarea-with-count"
        placeholder="Enter your discussion content..."
        rows="3"
        [maxlength]="CONTENT_MAX_LENGTH"
      ></textarea>
      <span
        class="content-char-count"
        [class.content-char-count--below-min]="contentLength > 0 && contentLength < CONTENT_MIN_LENGTH"
        [class.content-char-count--over-max]="contentLength > CONTENT_MAX_LENGTH"
        [class.content-char-count--valid]="contentLength >= CONTENT_MIN_LENGTH && contentLength <= CONTENT_MAX_LENGTH"
        title="Characters: current (min–max)"
      >
        {{ contentLength }} ({{ CONTENT_MIN_LENGTH }}-{{ CONTENT_MAX_LENGTH }})
      </span>
    </div>
    <div
      *ngIf="discussionForm.get('content')?.invalid && discussionForm.get('content')?.touched"
      class="error-message"
    >
      <span *ngIf="discussionForm.get('content')?.errors?.['required']">Content is required</span>
      <span *ngIf="discussionForm.get('content')?.errors?.['minlength']">Content must be at least {{ CONTENT_MIN_LENGTH }} characters</span>
    </div>
  </div>

  <!-- Custom Tags and Attachments in one row (both col-6) -->
  <div class="tags-attachments-row-group">
    <!-- Custom Tags (col-6) -->
    <div class="form-group form-group-col-6">
      <label for="tags" class="form-label">
        Custom Tags <span class="required-asterisk">*</span>
      </label>
      <div class="tags-input-wrapper">
        <div class="tags-input-container">
          <input
            type="text"
            id="tags"
            class="tags-input tags-input-with-add"
            placeholder="Enter a tag"
            [(ngModel)]="tagInput"
            [ngModelOptions]="{standalone: true}"
            (keydown)="onTagInputKeydown($event)"
            (input)="onTagInputChange()"
            (paste)="onTagInputPaste($event)"
          />
          <button
            type="button"
            class="tags-add-button tags-add-button--inside"
            (click)="addTag()"
            [disabled]="!canAddTag()"
            title="Add tag"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div class="tags-container" *ngIf="getTags().length > 0">
          <span
            *ngFor="let tag of getTags()"
            class="tag-chip"
            [class.tag-chip--duplicate]="isTagDuplicate(tag)"
          >
            {{ tag }}
            <svg
              class="tag-chip-close"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              (click)="removeTag(tag)"
            >
              <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </div>
      </div>
      <div
        *ngIf="discussionForm.get('tags')?.invalid && discussionForm.get('tags')?.touched"
        class="error-message"
      >
        At least one tag is required
      </div>
    </div>

    <!-- Attachments (col-6) -->
    <div class="form-group form-group-col-6">
      <label for="attachments" class="form-label">
        Attachments
      </label>
      <div class="attachment-wrapper">
        <label 
          *ngIf="!hasUploadedFiles()"
          for="fileInput" 
          class="attachment-upload-label"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <span>Upload Files</span>
          <span class="attachment-hint-inline">Allowed: {{ getAllowedFileTypesLabel() }}. Max size: 5MB per file.</span>
        </label>
        <input
          *ngIf="!hasUploadedFiles()"
          type="file"
          id="fileInput"
          class="attachment-input"
          accept=".pdf,.jpg,.jpeg,.png"
          multiple
          (change)="onFileSelected($event)"
        />
        
        <!-- Attachments List: green dashed border, upload icon, filename (truncated), red delete icon -->
        <div class="attachments-list" *ngIf="attachmentFiles.length > 0">
          <div *ngFor="let attachment of attachmentFiles; let i = index" class="attachment-item attachment-item--success" [class.attachment-item--uploading]="attachment.uploading" [class.attachment-item--error]="attachment.error">
            <svg class="attachment-item--upload-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            <span class="attachment-item--file-name" [title]="getAttachmentDisplayName(attachment)">{{ getAttachmentDisplayName(attachment) }}</span>
            <span class="attachment-item--status" *ngIf="attachment.uploading">Uploading...</span>
            <button
              type="button"
              class="attachment-item--delete-btn"
              (click)="removeAttachment(i)"
              title="Remove file"
              aria-label="Remove file"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </button>
            <span class="attachment-item--status attachment-item--status-error" *ngIf="attachment.error" [title]="attachment.error">{{ attachment.error }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Form Actions -->
  <div class="form-actions">
    <button type="button" class="btn-cancel" (click)="cancelForm()">Cancel</button>
    <button type="button" class="btn-publish" (click)="publishDiscussion()">{{ isEditMode ? 'Update' : 'Publish' }}</button>
  </div>
</form>
